import React, { createContext, type ReactNode, useCallback, useContext, useState } from 'react';

import { noop } from '@pkg/utils';

import { type PortalAreaScope, type PortalAreaUpdater, type PortalContextDescriptor } from './types';

export interface PortalProviderProps {
    children: ReactNode;
}

const DEFAULT_PORTAL_AREA_SCOPE: PortalAreaScope = {
    page: null,
    profile: null,
    toast: null,
    transition: null,
};

export const DEFAULT_PORTAL_CONTEXT: PortalContextDescriptor = {
    area: {
        ...DEFAULT_PORTAL_AREA_SCOPE,
    },
    setPortalArea: noop,
};

export const PortalContext = createContext<PortalContextDescriptor>({
    ...DEFAULT_PORTAL_CONTEXT,
});

export function PortalProvider({ children }: PortalProviderProps) {
    const setPortalArea: PortalAreaUpdater = useCallback(([key, value]) => {
        updatePortalArea((prevState) => {
            const { area, ...state } = prevState;
            const newState: PortalContextDescriptor = {
                ...state,
                area: {
                    ...area,
                    [key]: value,
                },
            };

            return newState;
        });
    }, []);

    const initialState: PortalContextDescriptor = {
        ...DEFAULT_PORTAL_CONTEXT,
        setPortalArea,
    };

    const [portalState, updatePortalArea] = useState(initialState);

    return <PortalContext.Provider value={portalState}>{children}</PortalContext.Provider>;
}

export function usePortal() {
    return useContext(PortalContext);
}
